/**
 *
 * MerchantProductFe
 *
 */
import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import Helmet from 'react-helmet'
import { connect } from 'react-redux'
import { AnalyticsProvider } from 'Hoc/AnalyticsProvider'
import { LanguageProvider } from 'Hoc/LanguageProvider'
import WithProfilingCheck from 'Hoc/WithProfilingCheck'
import flowPopupMap, { flowPopupMapType } from 'Utility/flowPopupMap'
import { doShowPopup } from 'Store/actions/popup'
import { doSaveFirebaseToken, doGetMinAppVersion } from 'Store/actions/global'
import { elementsMap } from 'Utility/TTLmapping'
import AcgFlow from 'Components/AcgFlow'
import flowToastMap from 'Utility/flowToastMap'
import flowFloatingMap from 'Utility/flowFloatingMap'
import { Capacitor } from '@capacitor/core'
import AppInit from 'Hoc/AppInit'
import uciSearchMetadata from 'Utility/uciSearchMetadata'

const MerchantProductFeContainer = ({
  authRoutes,
  unauthRoutes,
  logged,
  popupKey,
  ttlKey,
  toastKey,
  floatingKey,
  language,
  doShowPopup,
  saveFirebaseToken,
  minAppVersion,
  doGetMinAppVersion,
  contactKey
}) => {
  const flowPopup = popupKey ? flowPopupMap[popupKey] : ''
  const flowTtl = ttlKey ? elementsMap[ttlKey] : ''
  const flowToast = toastKey ? flowToastMap[toastKey] : ''
  const flowFloating = floatingKey ? flowFloatingMap[floatingKey] : ''
  const loggedApp = authRoutes.routes.map(({ component }) => component())
  const [isAppAvailable, setIsAppAvailable] = useState(true)

  const getTitle = (bank) => {
    switch (bank) {
      case 'NEXIBUSINESS':
        return 'Nexi Business'
      case 'UCI':
        return 'UniCredit - Nuovo Portale Esercenti'
      default:
        return 'Merchant Portal'
    }
  }

  const returnElement = () => {
    if (window.sessionStorage.getItem('jwe')) {
      const loggedCheck = !!window.sessionStorage.getItem('jwe')
      return <WithProfilingCheck logged={loggedCheck}>{loggedApp}</WithProfilingCheck>
    } else {
      if (Capacitor.isNativePlatform()) {
        let pathToRedirect = '/login-app'
        if (window.sessionStorage.getItem('fromMosDL')) pathToRedirect = '/login-mos-app'
        return <Navigate to={pathToRedirect} />
      } else {
        return <AcgFlow />
      }
    }
  }

  const { pathname } = useLocation()
  const canonicalPaths = {
    '/': '/',
    '/login': '/login',
    '/registration': '/registration/vat-email',
    '/registration/vat-email': '/registration/vat-email',
    '/support-pre-login': '/support-pre-login'
  }
  const canonicalPath = canonicalPaths[pathname.replace(/\/+$/, '') || '/']
  const canonicalUrl = process.env.BANK === 'UCI' && process.env.APPLICATION_URL && canonicalPath
    ? new URL(canonicalPath, process.env.APPLICATION_URL).href
    : null
  const publicPath = pathname.replace(/\/+$/, '') || '/'
  const hasUciSearchMetadata = process.env.BANK === 'UCI' && process.env.APPLICATION_URL &&
    (publicPath === '/' || publicPath === '/login')
  const title = getTitle(process.env.BANK)

  return (
    <>
      <Helmet titleTemplate={`%s - ${title}`} defaultTitle={title}>
        <meta name='description' content={hasUciSearchMetadata ? uciSearchMetadata.description : 'Merchant Portal'} />
        {hasUciSearchMetadata && <script type='application/ld+json'>{JSON.stringify(uciSearchMetadata.structuredData(process.env.APPLICATION_URL, publicPath))}</script>}
        {canonicalUrl && <link rel='canonical' href={canonicalUrl} />}
        {canonicalUrl && <meta property='og:url' content={canonicalUrl} />}
      </Helmet>
      {Capacitor.isNativePlatform() &&
        <AppInit
          contactKey={contactKey}
          onNetworkDisconnected={() => {
            setIsAppAvailable(false)
            doShowPopup(flowPopupMapType.BLOCKED_OFFLINE_APP)
          }}
          getAppVersions={() => doGetMinAppVersion()}
          minVersion={minAppVersion}
          showPopUpShouldUpdateApp={() => {
            setIsAppAvailable(false)
            doShowPopup(flowPopupMapType.BLOCKED_MUST_UPDATE_APP)
          }}
          showPopUpRooted={() => {
            setIsAppAvailable(false)
            doShowPopup(flowPopupMapType.BLOCKED_ROOTED_APP)
          }}
          showPopUpSslInvalid={() => {
            setIsAppAvailable(false)
            doShowPopup(flowPopupMapType.BLOCKED_SSL_INVALID_APP)
          }}
          logged={!!window.sessionStorage.getItem('jwe')}
          sendAppId={(token, permission) => {
            saveFirebaseToken({ token, permission })
          }}
        />}
      <LanguageProvider language={language}>
        <AnalyticsProvider>
          {isAppAvailable && (!Capacitor.isNativePlatform() || minAppVersion !== '') &&
            <Routes>
              {unauthRoutes.routes.map(({ component }) => component())}
              <Route path='*' element={returnElement()} />
            </Routes>}
          {flowPopup}
          {flowTtl}
          {flowToast}
          {flowFloating}
        </AnalyticsProvider>
      </LanguageProvider>
    </>
  )
}

MerchantProductFeContainer.propTypes = {
  authRoutes: PropTypes.objectOf(
    PropTypes.arrayOf(
      PropTypes.shape({
        component: PropTypes.func.isRequired
      })
    )
  ).isRequired,
  unauthRoutes: PropTypes.objectOf(
    PropTypes.arrayOf(
      PropTypes.shape({
        component: PropTypes.func.isRequired
      })
    )
  ).isRequired,
  logged: PropTypes.bool.isRequired,
  popupKey: PropTypes.string,
  ttlKey: PropTypes.string,
  minAppVersion: PropTypes.string,
  contactKey: PropTypes.string,
  toastKey: PropTypes.string,
  floatingKey: PropTypes.string,
  language: PropTypes.string,
  doShowPopup: PropTypes.func,
  doGetMinAppVersion: PropTypes.func,
  saveFirebaseToken: PropTypes.func
}

const mapStateToProps = (state) => ({
  popupKey: state.popup.popupKey,
  minAppVersion: state.global.minAppVersion,
  contactKey: state.userProfiling.profiling.mktChiavePv,
  ttlKey: state.triggerTimeline.ttlKey,
  toastKey: state.toast.toastKey,
  floatingKey: state.floating.floatingKey,
  language: state.language.currentLanguage
})

const mapDispatchToProps = dispatch => ({
  doShowPopup: (type) => dispatch(doShowPopup(type)),
  doGetMinAppVersion: () => dispatch(doGetMinAppVersion()),
  saveFirebaseToken: (payload) => dispatch(doSaveFirebaseToken(payload))
})

export default connect(mapStateToProps, mapDispatchToProps)(MerchantProductFeContainer)
